import Head from 'next/head';
import { gql } from '@apollo/client';
import { useRouter } from 'next/router';
import { useEffect, useMemo } from 'react';
import { useAuth0User } from '@hooks/useAuth0User';
import { useCaptureCouponFromUrl } from '@hooks/useCaptureCouponFromUrl';

import { BodyRegular, H1, H4 } from '@components/atoms/typography';
import { getMembershipIds, getYoastHeadSEO, isPaidMember } from '@lib/utils';
import { client, getPageSEOByURI } from 'apollo-client';
import { reportIsrRegenerationFailure } from '@lib/isr/report-regeneration-failure';
import { FAQ } from '@components/join/UIElements';
import ReviewCard from '@components/ReviewCard';
import { useSubscriptionPlans } from '@hooks/useSubscriptionPlans';
import { useGeoCurrency } from '@hooks/useGeoCurrency';
import {
  getPriceForCurrency,
  JOIN_PAGE_REVIEWS,
  JoinTableInfo
} from '@lib/page-utils/Join';
import Carousel from '@components/carousel';
import useWindowSize from '@hooks/useWindowSize';
import JoinTable from '@components/JoinTable';
import JoinCard from '@components/JoinCard';
import VideoAdvert from '@components/video-advert';
import { gtagConversionEvent } from '@lib/gtag';
import { TwitterEvents } from '@lib/twitter-pixel';
import { getMarketingCopy } from '@lib/sanity';
import type { MarketingCopy } from '@lib/sanity';
import ReviewsHorizontal from '@components/reviews-horizontal';

type JoinProps = {
  page: any;
  marketingCopy: MarketingCopy;
};

const Join = ({ page, marketingCopy }: JoinProps) => {
  const { pathname } = useRouter();
  const yoastHead = getYoastHeadSEO(page?.seo?.fullHead, pathname);
  const { width } = useWindowSize();
  const { user } = useAuth0User();
  const userMembershipIds = getMembershipIds(user);
  // Paid members changing plan are charged full price (the promo discount is
  // for new subscribers only), so they must see full prices here (FFH-2262).
  const shouldShowFullPriceOnly = isPaidMember(user);
  // TODO(FFH-1298): wire twitterHasLoaded to a Zustand readiness flag set by the Twitter pixel script onLoad in _app.tsx.
  const twitterHasLoaded = false;
  const { plans, isLoading: plansAreLoading } = useSubscriptionPlans([
    'starter_yearly',
    'pro_yearly',
    'ultra_yearly'
  ]);
  const { currencyCode } = useGeoCurrency();

  useEffect(() => {
    gtagConversionEvent({
      eventType: 'initiate_checkout'
    });
  }, []);

  useEffect(() => {
    if (twitterHasLoaded) {
      window.twq('event', TwitterEvents.checkoutInitiated);
    }
  }, [twitterHasLoaded]);

  useCaptureCouponFromUrl();

  const updatedJoinTableInfo = useMemo(() => {
    if (!plans || !JoinTableInfo) return JoinTableInfo;

    return {
      ...JoinTableInfo,
      tableHeaders: JoinTableInfo.tableHeaders.map((header) => {
        if (!header.lookup_key) return header;

        const result = getPriceForCurrency(
          plans,
          header.lookup_key,
          currencyCode,
          { discountPercent: marketingCopy.promoDiscountPercent }
        );
        if (!result) return header;
        const { formattedPriceWithSymbol, comparisonFormattedPriceWithSymbol } =
          result;

        return {
          ...header,
          discountedAnnualPerMonthPrice: formattedPriceWithSymbol,
          fullAnnualPerMonthPrice: comparisonFormattedPriceWithSymbol
        };
      })
    };
  }, [plans, currencyCode, marketingCopy.promoDiscountPercent]);

  return (
    <>
      <div className="bg-neutral-50">
        <Head>{yoastHead}</Head>
        {/* Header section */}
        <div className="container mx-auto px-4 md:px-16 pt-6 md:pt-16">
          <div className="flex flex-col">
            <H1>{marketingCopy.join.title}</H1>
            <div className="mb-6 flex flex-col justify-center">
              <H4>{marketingCopy.join.subtitle}</H4>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 md:px-12">
          <div className="lg:w-1/3">
            <ReviewsHorizontal />
          </div>
        </div>

        {/* Pricing tables section */}
        <div className="container mx-auto px-4 md:px-16">
          <div className="mt-12 hidden lg:block">
            <JoinTable
              tableHeaders={updatedJoinTableInfo.tableHeaders}
              rows={JoinTableInfo.rows}
              userMembershipIds={userMembershipIds}
              plansAreLoading={plansAreLoading}
              showFullPriceOnly={shouldShowFullPriceOnly}
            />
          </div>
          <div className="mt-6 block lg:hidden space-y-6">
            {updatedJoinTableInfo.tableHeaders
              .filter(({ header }) => Boolean(header))
              .map(
                ({
                  subHeader,
                  header,
                  discountedAnnualPerMonthPrice,
                  yearlyId,
                  fullAnnualPerMonthPrice
                }) => (
                  <JoinCard
                    key={yearlyId}
                    title={header}
                    subHeader={subHeader}
                    discountedAnnualPerMonthPrice={
                      discountedAnnualPerMonthPrice
                    }
                    fullAnnualPerMonthPrice={fullAnnualPerMonthPrice}
                    priceId={yearlyId}
                    listItems={updatedJoinTableInfo.rows
                      .filter((row) => Boolean(row[header.toLowerCase()]))
                      .map((row) => row.text)}
                    userMembershipIds={userMembershipIds}
                    variant="join"
                    plansAreLoading={plansAreLoading}
                    showFullPriceOnly={shouldShowFullPriceOnly}
                  />
                )
              )}
          </div>
        </div>

        {/* User testimonials section */}
        <div className="bg-neutral-100">
          <div className="container mx-auto px-8 md:px-16 py-6 md:py-16 md:pt-12">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-5 md:mb-3">
              <div>
                <H1>What our users say</H1>
                <BodyRegular className="pb-8">
                  See what we've done for Fantasy Football fans like you
                </BodyRegular>
              </div>
            </div>
            <div>
              <Carousel
                slidesPerView={width < 978 ? 1.01 : 3.2}
                id="join-reviews"
              >
                {JOIN_PAGE_REVIEWS.map(({ userName, reviewText }) => (
                  <div className="mr-4 md:mr-8" key={userName}>
                    <ReviewCard userName={userName} reviewText={reviewText} />
                  </div>
                ))}
              </Carousel>
            </div>
          </div>
        </div>
        <VideoAdvert variant="video" />
      </div>

      {/* FAQ section */}
      <div className="bg-neutral-100">
        <div className="container mx-auto px-8 md:px-16 pt-6 md:pt-12">
          <div className="py-16 bg-neutral-100">
            <H1>Frequently asked questions</H1>
            <div className="px-0 md:px-28 py-4 md:py-8 ">
              <FAQ />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const JOIN_REVALIDATE_SECONDS = 300;

export const getStaticProps = async () => {
  try {
    const { data } = await client.query({
      query: gql`
        ${getPageSEOByURI('join')}
      `
    });

    const page = data.pageBy;
    const marketingCopy = await getMarketingCopy();

    return {
      props: { page, marketingCopy },
      revalidate: JOIN_REVALIDATE_SECONDS
    };
  } catch (error) {
    return reportIsrRegenerationFailure('/join', error);
  }
};

export default Join;
